import type { GitMetadataAncestor, Issue } from "./issue.types"

/**
 * Determines if an issue has all findings resolved in the current scope
 * but the issue status remains open due to other scopes being affected
 */
export const isIssueResolvedInScopeAndNotGlobally = (issue: Issue) => {
  if (!issue) return false
  const { findings, isResolved } = issue || {}
  const findingsCount = findings?.length || 0
  // Check if issue has findings in this scope and is not resolved globally
  if (findingsCount === 0 || isResolved === true) return false
  // Check if all findings are resolved
  return findings.every((finding) => finding.isResolved)
}

/**
 * Determines if an issue is fully resolved (is_resolved = true)
 * to show the resolved banner at the top
 */
export const isIssueFullyResolved = (issue: Issue): boolean => {
  if (!issue) return false

  // Use the isResolved boolean field
  return issue.isResolved === true
}

// Normalizes a version for tolerant matching: strips a leading "v" and any build-metadata suffix.
const normalizeVersion = (version?: string): string =>
  (version ?? "").trim().replace(/^v/, "").replace(/\+.*$/, "")

/**
 * Correlates an introduced-through root to its ancestor by name + normalized version,
 * else the sole same-name ancestor. Returns undefined when nothing matches.
 */
export const findGitMetadataForRoot = (
  ancestors: GitMetadataAncestor[] | undefined,
  name: string,
  version?: string
): GitMetadataAncestor | undefined => {
  const byName = ancestors?.filter((entry) => entry.name === name) ?? []
  if (byName.length === 0) return undefined

  const target = normalizeVersion(version)
  const exact = byName.find((entry) => normalizeVersion(entry.version) === target)
  if (exact) return exact

  return byName.length === 1 ? byName[0] : undefined
}
